import { z } from 'zod'

// ─── Utilities ───

/** Standard paginated response wrapper. */
export function paginatedSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  })
}

/** Standard error shape returned by Edge APIs. */
export const ApiErrorSchema = z.object({
  message: z.string(),
  code: z.string().optional(),
  statusCode: z.number().optional(),
})

export type ApiError = z.infer<typeof ApiErrorSchema>

// ─── Ticker ───

/** Ticker search result from proxy API. */
export const TickerSearchResultSchema = z.object({
  symbol: z.string(),
  name: z.string(),
})

export type TickerSearchResult = z.infer<typeof TickerSearchResultSchema>

/** Ticker verification result from data API. */
export const TickerVerificationSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  company: z
    .object({
      companyName: z.string(),
    })
    .optional(),
  error: z.boolean().optional(),
})

export type TickerVerification = z.infer<typeof TickerVerificationSchema>

// ─── Field Definition ───

/** Human-readable definition + example for a column key. Null when missing. */
export const FieldDefinitionSchema = z.object({
  field_definition: z.string(),
  example: z.string(),
})

export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>

// ─── User ───

export const TickerHistoryEntrySchema = z.object({
  symbol: z.string(),
  companyName: z.string(),
})

export type TickerHistoryEntry = z.infer<typeof TickerHistoryEntrySchema>

export const SubscriptionSchema = z.object({
  subscriptionId: z.string(),
  subscriptionStatus: z.string(),
  // Start of the current billing period (unix seconds). For a past_due sub this ≈ when
  // the renewal failed, so it anchors the grace window (see subscription-state.ts).
  currentPeriodStart: z.number().nullish(),
  // True when the professional market-data add-on is on the subscription. The
  // auth guard compares this to the user's latest survey to detect a status flip.
  professional: z.boolean().optional(),
  // A period-end scheduled change (e.g. a downgrade), mirrored from a Stripe
  // subscription schedule so the billing page can show "switching to X on <date>".
  pendingChange: z
    .object({
      priceId: z.string().optional(),
      planLevel: z.string().optional(),
      amount: z.number().optional(),
      interval: z.string().optional(),
      effectiveAt: z.number().optional(),
    })
    .nullish(),
  // When the plan is set to cancel at period end (unix seconds), written by the
  // webhook; cleared (null or absent) when the customer resumes it.
  cancelAt: z.number().nullish(),
  // Fields are nullish: DynamoDB stores explicit nulls, and webhook events with an
  // unexpanded price.product can leave name/amount null. Tolerate it, don't crash.
  planDetails: z.object({
    amount: z.number().nullish(),
    currency: z.string().nullish(),
    interval: z.string().nullish(),
    interval_count: z.number().nullish(),
    plan_level: z.string().nullish(),
    name: z.string().nullish(),
  }),
})

export type Subscription = z.infer<typeof SubscriptionSchema>

export const UserSchema = z.object({
  givenName: z.string().optional().default(''),
  familyName: z.string().optional().default(''),
  userSub: z.string(),
  // Stable internal id — a hard invariant the backend always resolves + returns
  // (== userSub until a pool migration diverges it). cognitoSub is informational.
  edgeUserId: z.string(),
  cognitoSub: z.string().optional(),
  ihmac: z.string().nullable().optional().default(null),
  // Local-dev / test-stage only: the customer's Stripe test-clock time (unix seconds).
  // When present, time-based gating evaluates against it instead of the real clock so
  // clock advances are reflected. Absent (null) in production → real time is used.
  testClockTime: z.number().nullish(),
  hasTrialed: z.boolean().optional().default(false),
  betaAccess: z.boolean().optional().default(false),
  tickerHistory: z.array(TickerHistoryEntrySchema).optional().default([]),
  selectedTicker: z.string().optional().default(''),
  // Selected color theme id (e.g. 'dark', 'glacier'), synced across devices.
  // Absent/null for users who've never picked one → local default is used.
  themePreference: z.string().nullish(),
  // How SEC filings are painted on the Research filing page ('theme' |
  // 'original' | 'paper'). Loosely typed: the sync hook validates before applying.
  filingReadingMode: z.string().nullish(),
  // App text size as a whole percent (90 | 100 | 110), synced across
  // devices. Loosely typed: the sync hook validates before applying.
  textScale: z.number().nullish(),
  // Global chart interaction settings (wheel/drag/zoom-anchor grammar), synced
  // across devices. Absent/null for users who've never picked one → local
  // default (DEFAULT_INTERACTION_SETTINGS) is used. Loosely typed here — the
  // sync hook validates individual keys before applying.
  chartInteraction: z.record(z.unknown()).nullish(),
  chartIndicatorDefaults: z.record(z.unknown()).nullish(),
  // Learn curriculum progress (completed lesson ids + active track/position).
  // Absent/null for users who've never started a track. Loosely typed — the
  // learn-progress sync hook validates the shape before applying.
  learnProgress: z.record(z.unknown()).nullish(),
  // First-run onboarding state (interview answers, persona, checklist).
  // Absent/null for users who predate onboarding or haven't started. Loosely
  // typed: the onboarding sync hook validates the shape before applying.
  onboarding: z.record(z.unknown()).nullish(),
  subscriptions: z.array(SubscriptionSchema).optional().default([]),
  pendingSubscription: z.string().nullable().optional(),
  complianceFormEditLocked: z.boolean().optional(),
  complianceExpiresAt: z.string().optional(),
  // Professional status from the user's latest compliance survey. A mismatch
  // with the active subscription's `professional` flag means they re-answered
  // and must switch products (auth guard → /billing/switch).
  complianceProfessional: z.boolean().optional(),
  // Partner program signifier. "cobra" = a Cobra Premier member (100%-off
  // partner sub); the backend maintains it from the Stripe coupon via webhook.
  vendorId: z.string().nullish(),
})

export type User = z.infer<typeof UserSchema>

// ─── Plans ───

export const PlanSchema = z.object({
  plan: z.string(),
  interval: z.string(),
  unit_amount: z.number().optional(),
  currency: z.string().optional(),
  product_name: z.string().optional(),
  lookup_key: z.string().optional(),
})

export type Plan = z.infer<typeof PlanSchema>

// ─── Tokens ───

export const TokenBalanceSchema = z.object({
  balance: z.number(),
  lifetime_purchased: z.number(),
})

export type TokenBalance = z.infer<typeof TokenBalanceSchema>

// ─── Usage ───

export const UsageSchema = z.object({
  period_start: z.number(),
  plan_level: z.string(),
  ai_answer_credits_used: z.number().optional().default(0),
  ai_answer_credits_limit: z.number().optional().default(0),
  // Deep Research runs per billing cycle. The single Deep Research allowance
  // since the deep_dive_questions pool was retired (2026-09-09 audit, D11).
  research_runs_used: z.number().optional().default(0),
  research_runs_limit: z.number().optional().default(0),
  // Backtester runs per billing cycle. A limit of -1 is the server's unlimited
  // sentinel; 0 is a real ceiling of zero (a plan without the Backtester).
  backtest_runs_used: z.number().optional().default(0),
  backtest_runs_limit: z.number().optional().default(0),
})

export type Usage = z.infer<typeof UsageSchema>

export const QuotaCheckSchema = z.object({
  used: z.number(),
  // Null on a plan with no ceiling on the resource: the server stores an
  // UNLIMITED sentinel and reports it as null rather than as a negative
  // number, so a progress bar has nothing to divide by.
  limit: z.number().nullable(),
  remaining: z.number().nullable(),
  allowed: z.boolean(),
})

export type QuotaCheck = z.infer<typeof QuotaCheckSchema>

// ─── Entitlements ───

/**
 * Every plan limit and feature flag, served by `GET /v2/users/me/entitlements`.
 * Mirrors the `Entitlements` pydantic model in
 * `apps/edge-user-api/edge_user_api/services/entitlements.py`, which is the single
 * source of truth — the frontend's DEFAULT_* tables are only its offline stand-in.
 *
 * `null` on a numeric limit means UNLIMITED (Pro+ custom layouts), or "not
 * applicable" when the paired `*_enabled` flag is false (export caps on Basic).
 * `0` is a real ceiling of zero. `PlanConstraintProvider` maps null to `Infinity`
 * so existing `x < limit` comparisons are unchanged.
 */
export const EntitlementsSchema = z.object({
  custom_layouts_max: z.number().nullable(),
  scanner_panels_per_layout_max: z.number().nullable(),
  scanner_rows_max: z.number().nullable(),
  scanner_poll_interval_ms: z.number(),
  expressions_max: z.number().nullable(),
  history_max_months: z.number().nullable(),
  export_enabled: z.boolean(),
  export_max_rows: z.number().nullable(),
  export_max_columns: z.number().nullable(),
  backtest_enabled: z.boolean(),
  backtest_runs_per_month: z.number().nullable(),
  backtest_tickers_per_run: z.number().nullable(),
  backtest_history_days: z.number().nullable(),
  ai_answer_credits: z.number(),
  research_runs: z.number(),
  /** Sorted feature ids this plan grants. A superset of the local `Feature` union. */
  features: z.array(z.string()),
})

export type Entitlements = z.infer<typeof EntitlementsSchema>

/** `plan_level` is the RESOLVED plan: an unknown id reports `basic` with basic limits. */
export const EntitlementsResponseSchema = z.object({
  plan_level: z.string(),
  entitlements: EntitlementsSchema,
})

export type EntitlementsResponse = z.infer<typeof EntitlementsResponseSchema>

// ─── News / SEC ───

/** Matches the API's NewsArticleItem schema from POST /v2/news */
export const NewsArticleSchema = z
  .object({
    hash: z.string().nullable().optional(),
    ticker: z.string().nullable().optional(),
    tickers: z.array(z.string()).nullable().optional(),
    published_utc: z.string().nullable().optional(),
    combinedHeadline: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    story_url: z.string().nullable().optional(),
    is_pr: z.boolean().nullable().optional(),
    similarity_id: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform((v) => (v != null ? String(v) : v)),
    sec_form_type: z.string().nullable().optional(),
    /** SEC rows only: addresses the filing in the Research filing viewer. */
    accession_no: z.string().nullable().optional(),
    quote: z.record(z.string(), z.record(z.string(), z.unknown())).nullable().optional(),
  })
  .passthrough()

export type NewsArticle = z.infer<typeof NewsArticleSchema>

// ─── News Summary ───

export const NewsSummarySchema = z
  .object({
    tldr: z.string().optional(),
    summary: z.string().optional(),
    key_points: z.array(z.string()).optional(),
    // Fields from edge-research POST /v1/news/summarize response
    summary_version: z.number().optional(),
    model: z.string().optional(),
    source_type: z.string().optional(),
    status: z.string().optional(),
    // Legacy fields from edge-financial-api (kept for backward compat / mock data)
    key_dates: z
      .array(
        z.object({
          date: z.string(),
          description: z.string(),
        }),
      )
      .optional(),
    similarity: z.number().optional(),
    is_press_release: z.boolean().optional(),
    is_contract_win: z.boolean().optional(),
    is_company_receiving_money: z.boolean().optional(),
    is_fda_approval: z.boolean().optional(),
    impact_score: z.number().optional(),
    document_url: z.string().optional(),
    datetime: z.string().optional(),
    vendor: z.string().optional(),
  })
  .passthrough()

export type NewsSummary = z.infer<typeof NewsSummarySchema>

// ─── SEC Filings ───

export const SecDocumentFileSchema = z
  .object({
    size: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    document: z.string().nullable().optional(),
    sequence: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
  })
  .passthrough()

export type SecDocumentFile = z.infer<typeof SecDocumentFileSchema>

export const SecDataFileSchema = z
  .object({
    size: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    documentUrl: z.string().nullable().optional(),
    sequence: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
  })
  .passthrough()

export type SecDataFile = z.infer<typeof SecDataFileSchema>

export const SecFilingItemSchema = z.object({
  hash: z.string().nullable().optional(),
  accession_no: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  published_utc: z.string().nullable().optional(),
  combinedHeadline: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  sec_form_type: z.string().nullable().optional(),
  document_format_files: z.array(SecDocumentFileSchema).nullable().optional(),
  data_files: z.array(SecDataFileSchema).nullable().optional(),
})

export type SecFilingItem = z.infer<typeof SecFilingItemSchema>

/**
 * Top-N most recent SEC filings per category for a ticker. Returned by
 * `GET /v2/sec/filings/{ticker}/by-category`. Each value is at most `n`
 * filings (the route's query parameter), ordered by `published_utc` DESC.
 *
 * The category keys here are the source of truth for which UI buckets
 * exist — the frontend's `FILING_CATEGORIES` constant adds labels and
 * icons but must stay in sync with this list and the backend's
 * `CATEGORY_FORM_TYPES`.
 */
export const FilingsByCategoryResponseSchema = z.object({
  earnings: z.array(SecFilingItemSchema).default([]),
  news: z.array(SecFilingItemSchema).default([]),
  dilution: z.array(SecFilingItemSchema).default([]),
  proxy: z.array(SecFilingItemSchema).default([]),
  ownership: z.array(SecFilingItemSchema).default([]),
})

export type FilingsByCategoryResponse = z.infer<typeof FilingsByCategoryResponseSchema>

export const FilingDiffStatsSchema = z.object({
  added: z.number(),
  removed: z.number(),
})

export const FilingDiffMetaSchema = z.object({
  accession_no: z.string().nullable().optional(),
  hash: z.string().nullable().optional(),
  form_type: z.string().nullable().optional(),
  published_utc: z.string().nullable().optional(),
  headline: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
})

export const FilingDiffResponseSchema = z.object({
  diff_html: z.string(),
  stats: FilingDiffStatsSchema,
  older: FilingDiffMetaSchema,
  newer: FilingDiffMetaSchema,
})

export type FilingDiffStats = z.infer<typeof FilingDiffStatsSchema>
export type FilingDiffMeta = z.infer<typeof FilingDiffMetaSchema>
export type FilingDiffResponse = z.infer<typeof FilingDiffResponseSchema>

// ─── SEC Filing Summary (AI) ───

export const SecFilingSummarySchema = z
  .object({
    tldr: z.string(),
    summary: z.string(),
    key_points: z.array(z.string()).default([]),
    form_type: z.string(),
    summary_version: z.number().optional(),
    model: z.string().optional(),
    origin: z.string().optional(),
    status: z.string().optional(),
  })
  .passthrough()

export type SecFilingSummary = z.infer<typeof SecFilingSummarySchema>

// ─── Ticker Daily Summary ("Why It's Moving") ───

export const TickerSummaryKeyPointSchema = z.object({
  text: z.string(),
  source_refs: z.array(z.string()).default([]),
})

export const TickerSummarySourceSchema = z
  .object({
    ref: z.string(),
    type: z.string(),
    db_id: z.number(),
    // The backend response model emits explicit `null` for unset optional
    // fields, so accept `null` as well as absent — `.nullish()` = optional + nullable.
    title: z.string().nullish(),
    url: z.string().nullish(),
    date: z.string().nullish(),
    news_id: z.string().nullish(),
    accession_no: z.string().nullish(),
    form_type: z.string().nullish(),
    fy: z.number().nullish(),
    q: z.number().nullish(),
  })
  .passthrough()

export const TickerSummarySchema = z
  .object({
    ticker: z.string(),
    summary_date: z.string(),
    tldr: z.string(),
    key_points: z.array(TickerSummaryKeyPointSchema).default([]),
    sentiment: z.string(),
    vs_expectations: z.string(),
    impact: z.string(),
    event_tags: z.array(z.string()).default([]),
    sources: z.array(TickerSummarySourceSchema).default([]),
    model: z.string().nullish(),
    prompt_version: z.number().nullish(),
    generated_at: z.string().nullish(),
    status: z.string().nullish(),
  })
  .passthrough()

export type TickerSummaryKeyPoint = z.infer<typeof TickerSummaryKeyPointSchema>
export type TickerSummarySource = z.infer<typeof TickerSummarySourceSchema>
export type TickerSummary = z.infer<typeof TickerSummarySchema>

// ─── FMP Transcripts ───

export const FmpTranscriptItemSchema = z.object({
  id: z.number(),
  ticker: z.string(),
  fiscal_year: z.number(),
  quarter: z.number(),
  published_utc: z.string().nullable().optional(),
  s3_key: z.string(),
  story_url: z.string().nullable().optional(),
  vendor_name: z.string(),
  eps_actual: z.number().nullable().optional(),
  eps_estimate: z.number().nullable().optional(),
  eps_surprise_pct: z.number().nullable().optional(),
  revenue_surprise_percent: z.number().nullable().optional(),
  earnings_date: z.string().nullable().optional(),
  announce_time: z.string().nullable().optional(),
})

export type FmpTranscriptItem = z.infer<typeof FmpTranscriptItemSchema>

export const TranscriptUrlResponseSchema = z.object({ url: z.string() })

// ─── FMP Transcript Enrichments (Sonnet summary pass) ───

export const TranscriptTakeawaySchema = z.object({
  title: z.string(),
  detail: z.string(),
  tags: z.array(z.string()).default([]),
})

export const TranscriptSentimentSchema = z.object({
  overall: z.enum(['bullish', 'bearish', 'neutral', 'mixed']),
  confidence: z.number().min(0).max(1),
  notes: z.string().nullable().optional(),
})

export const TranscriptGuidanceItemSchema = z.object({
  metric: z.string(),
  direction: z.enum(['raised', 'lowered', 'reiterated', 'introduced', 'withdrawn']),
  detail: z.string(),
})

/**
 * Transcript metadata + enrichment payload. The enrichment fields
 * (`summary_md`, `takeaways`, `sentiment`, `guidance`) are nullable —
 * `null` means the worker has not yet processed this transcript, NOT that
 * the transcript is missing. Render a "pending" state in that case.
 */
export const TranscriptEnrichmentSchema = z.object({
  transcript_id: z.number(),
  ticker: z.string(),
  fiscal_year: z.number(),
  quarter: z.number(),
  call_date: z.string().nullable().optional(),
  s3_key: z.string(),
  story_url: z.string().nullable().optional(),
  summary_md: z.string().nullable().optional(),
  takeaways: z.array(TranscriptTakeawaySchema).nullable().optional(),
  sentiment: TranscriptSentimentSchema.nullable().optional(),
  guidance: z.array(TranscriptGuidanceItemSchema).nullable().optional(),
  prompt_version: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  generated_at: z.string().nullable().optional(),
  enriched_s3_key: z.string().nullable().optional(),
})

export type TranscriptTakeaway = z.infer<typeof TranscriptTakeawaySchema>
export type TranscriptSentiment = z.infer<typeof TranscriptSentimentSchema>
export type TranscriptGuidanceItem = z.infer<typeof TranscriptGuidanceItemSchema>
export type TranscriptEnrichment = z.infer<typeof TranscriptEnrichmentSchema>

// ─── FMP Transcript text — parsed speaker-tagged blocks for inline render ───

export const TranscriptTextLineSchema = z.object({
  speaker: z.string(),
  content: z.string(),
})

export const TranscriptTextResponseSchema = z.object({
  transcript_id: z.number(),
  ticker: z.string(),
  fiscal_year: z.number(),
  quarter: z.number(),
  lines: z.array(TranscriptTextLineSchema),
})

export type TranscriptTextLine = z.infer<typeof TranscriptTextLineSchema>
export type TranscriptTextResponse = z.infer<typeof TranscriptTextResponseSchema>

// ─── Transcript Highlights (AI-generated stat cards) ───

export const TranscriptHighlightItemSchema = z.object({
  stat: z.string().min(1),
  change: z.string().min(1),
  subline: z.string().min(1),
})

export const TranscriptHighlightsSchema = z.object({
  key_highlights: z.array(TranscriptHighlightItemSchema),
  auto_detected_highlights: z.array(TranscriptHighlightItemSchema),
  takeaways: z.array(TranscriptTakeawaySchema),
  guidance: z.array(TranscriptGuidanceItemSchema),
  model: z.string(),
  prompt_version: z.string(),
  cached: z.boolean(),
  generated_at: z.string(),
})

export type TranscriptHighlightItem = z.infer<typeof TranscriptHighlightItemSchema>
export type TranscriptHighlights = z.infer<typeof TranscriptHighlightsSchema>

export type SummarizeTranscriptHighlightsRequest = {
  ticker: string
  fiscal_year: number
  quarter: number
  force?: boolean
}

// --- Per-section transcript schemas (v3 split) -------------------------------

export const KeyHighlightsResponseSchema = z.object({
  items: z.array(TranscriptHighlightItemSchema),
  model: z.string(),
  prompt_version: z.string(),
  cached: z.boolean(),
  generated_at: z.string(),
})

export const AutoDetectedHighlightsResponseSchema = z.object({
  items: z.array(TranscriptHighlightItemSchema),
  model: z.string(),
  prompt_version: z.string(),
  cached: z.boolean(),
  generated_at: z.string(),
})

export const TakeawaysResponseSchema = z.object({
  items: z.array(TranscriptTakeawaySchema),
  model: z.string(),
  prompt_version: z.string(),
  cached: z.boolean(),
  generated_at: z.string(),
})

export const QaPairItemSchema = z.object({
  question: z.string(),
  answer: z.string(),
  // Prompt allows an empty array when synthesis spans many speakers; the
  // backend always emits `[]` rather than omitting the key today, but we
  // default here so a future contract change can't 502 the frontend.
  speakers: z.array(z.string()).default([]),
})

export const QaPairsResponseSchema = z.object({
  items: z.array(QaPairItemSchema),
  model: z.string(),
  prompt_version: z.string(),
  cached: z.boolean(),
  generated_at: z.string(),
})

export type KeyHighlightsResponse = z.infer<typeof KeyHighlightsResponseSchema>
export type AutoDetectedHighlightsResponse = z.infer<typeof AutoDetectedHighlightsResponseSchema>
export type TakeawaysResponse = z.infer<typeof TakeawaysResponseSchema>
export type QaPairItem = z.infer<typeof QaPairItemSchema>
export type QaPairsResponse = z.infer<typeof QaPairsResponseSchema>

export type SectionRequest = {
  ticker: string
  fiscal_year: number
  quarter: number
  force?: boolean
}

// ─── Expressions ───

export const ExpressionArgSchema = z.object({
  column: z.string(),
})

export const ExpressionSchema = z.object({
  name: z.string(),
  label: z.string(),
  expression: z.string(),
  format: z.enum(['compact', 'fixed', 'percent']),
  symbol: z.enum(['currency', 'number', 'percent']),
  args: z.record(z.string(), ExpressionArgSchema),
})

export type Expression = z.infer<typeof ExpressionSchema>
